const { VisualParameters } = require("@/visualisation/visualParameters.js");
const { isAssignPossible } = require("@/simulation/automation/automator.js");
const { MiscVisuals } = require("@/visualisation/gantt/miscVisual.js");
const { ProjectVisual } = require("@/visualisation/gantt/projectVisual.js");
const { ToolTipVisual } = require("@/visualisation/gantt/ToolTipVisual.js");
const { AssignHandler } = require("@/visualisation/assignHandler.js");
const { ResourceGroupVisual } = require("@/visualisation/resources/resourceGroupVisual.js")
const d3 = require("d3");
const { easeQuadInOut, easeLinear } = require("d3");
const { XLocationToHour, hourToXLocation, getTranslation } = require("./visualHelperFunctions.js")
const { parseSvg } = require("d3-interpolate/src/transform/parse");

class Visualiser {
    constructor(simulator, parentDiv, mounted, resourceComponents, prevTransform, durationValue, topbarComponent, state) {
        this.mounted = mounted;
        this.resourceComponents = resourceComponents;
        this.topbarComponent = topbarComponent;
        this.parentDiv = parentDiv;
        this.simulator = simulator;
        this.state = state;
        this.transform = prevTransform;

        var speedValue = this.topbarComponent.getSpeedValue();
        this.params = new VisualParameters(durationValue, speedValue);
        this.assignHandler = new AssignHandler(this);

        this.projectVisuals = [];
        this.lastFinishedProjectAmount = this.getFinishedProjectsAmount();
        this.createSVG(prevTransform);
        this.visualise(false);

        this.futureLoadGraphIntervalID = null;

        this.lastSwapTime = 0;


        if(this.simulator.params.allowPortfolioPlanning){
            this.createFutureLoadUpdateLoop();
        }
    }

    createFutureLoadUpdateLoop(){
        this.futureLoadGraphIntervalID = setInterval(() => {
            if(this.state.futureLoadGraph == null){
                return;
            }

            if(this.state.manuallyPaused){
                return;
            }

            this.state.futureLoadGraph.updateData();

        }, this.params.updateFutureLoadGraphTime);
    }

    //When reloading the pipeline page, we don't have to recreate the visualiser, reload it with the new divs
    reloadVisualiser(resourceComponents, topbarComponent, parentDiv, prevTransform) {
        this.resourceComponents = resourceComponents;
        this.topbarComponent = topbarComponent;
        this.parentDiv = parentDiv;
        this.oldProjectVisuals = this.projectVisuals;
        this.projectVisuals = [];

        this.createSVG(prevTransform);
        this.visualise(true);

        this.svg.call(this.zoom.transform, this.newTransform);
    }

    unloadVisualiser(){
        clearTimeout(this.futureLoadGraphIntervalID);
        this.svg.remove();

    }

    //Spawn new projects
    addNewProjects(reload) {
        var newProjects = this.simulator.projectPool.projects;
        if(reload && this.oldProjectVisuals != null){
            newProjects = [];

            this.oldProjectVisuals.forEach((oldProjectVisual) => {
            newProjects.push(oldProjectVisual.project);
            });

        }



        for (var projectIndex = this.projectVisuals.length; projectIndex < newProjects.length; projectIndex++) {
            var currProject = newProjects[projectIndex];
            this.projectVisuals.push(new ProjectVisual(this, currProject, this.getUnfinishedProjectCount()))
        }
    }

    getFinishedProjectsAmount() {
        var finished = 0;
        this.projectVisuals.forEach((currProject) => {
            //isFinished
            if (!currProject.project.isFinished()) {
                return;
            }
            finished += 1;
        });

        return finished;
    }
    getUnfinishedProjectCount() {
        var unfinished = 0;
        this.projectVisuals.forEach((currProject) => {
            //isFinished
            if (currProject.project.isFinished()) {
                return;
            }
            unfinished += 1;
        });

        return unfinished;
    }

    //Get the index of the projectVisual
    getProjectVisualIndex(projectVisual) {
        return this.projectVisuals.indexOf(projectVisual);
    }


    sortProjects(instant, speedmultiplier = 1) {
        var unfinished = 0;
        this.projectVisuals.forEach((currProject, index) => {
            //isFinished
            if (currProject.project.isFinished()) {
                return;
            }

            //Update the project data
            var prevYLevel = currProject.getProjectGroupYLocation(false);
            currProject.projectIndex = unfinished;
            currProject.updateProjectRowCount();

            //Transition the projects
            var currYLevel = currProject.getProjectGroupYLocation(false);
            var distance = prevYLevel - currYLevel;

                var time = distance * 2 / speedmultiplier;
            
            if (instant) {
                currProject.parentGroup.call(() => currProject.updateCulling(true)).attr("transform", `translate(${0}, ${currYLevel})`);
            }
            else {
            currProject.parentGroup.transition().call(() => currProject.updateCulling(true)).ease(easeLinear).duration(time).attr("transform", `translate(${0}, ${currYLevel})`);
            }
            unfinished += 1;
        });
    }

    visualise(reload) {
        this.miscVisuals = new MiscVisuals(this);

        this.addNewProjects(reload);
        this.resourceGroupVisuals = {}
        for (const [name, resourceComponent] of Object.entries(this.resourceComponents)) {
            this.resourceGroupVisuals[name] = new ResourceGroupVisual(this, resourceComponent, this.simulator.resourcePool.getResourceGroupFromName(name));
        }

        this.resetViewPort();
        this.updateElements();
    }

    updateSVGSize(width, height) {
        this.svg.attr("width", width).attr("height", height)
    }


    resetViewPort() {
        this.svg.transition()
            .ease(d3.easeCubicInOut)
            .duration(750)
            .call(this.zoom.transform, d3.zoomIdentity);
    }

    updateCulling() {
        //Check if we should render culled projects
        this.projectVisuals.forEach(currProjectVisual => {
            currProjectVisual.updateCulling();
        })
    }

    getSVGBounds(transform) {
        var returnBounds = {}
        returnBounds.top = (transform.y / transform.k) * -1 - this.params.paddingTop;
        returnBounds.left = (transform.x / transform.k) * -1 - this.params.paddingLeft;
        returnBounds.right = returnBounds.left + (parseFloat(this.parentDiv.clientWidth) / transform.k);
        returnBounds.bottom = (parseFloat(this.parentDiv.clientHeight) - transform.y) / transform.k - this.params.paddingTop;

        return returnBounds;
    }

    updateFutureLoadGraphCamera(duration) {
        if (this.state.futureLoadGraph == null || this.newTransform == null) {
            return;
        }

        if(!this.state.simulator.params.allowPortfolioPlanning){
            return;
        }

        
        this.state.futureLoadGraph.updateDomainFromGantt(this.calculateFutureloadGraphCamera(), duration)

    }

    calculateFutureloadGraphCamera() {
        if(this.newTransform == 0){
            return [0,1];
        }
        
        var newBounds = this.getSVGBounds(this.newTransform);

        var hourLeft = XLocationToHour(newBounds.left, this);
        var hourRight = XLocationToHour(newBounds.right, this);
        


        var paddedHourLeft = hourLeft + this.params.paddingLeft / (this.newTransform.k * this.params.xScaling)
        var paddedHourRight = hourRight - this.params.paddingRight / (this.newTransform.k * this.params.xScaling)

        return [paddedHourLeft / 8, paddedHourRight / 8];

    }


    createSVG() {
        var self = this;

        function handleZoom(e) {
            if (e == null) {
                return;
            }

            self.newTransform = e.transform;
            
            self.svgTop = (e.transform.y / e.transform.k) * -1 - self.params.paddingTop;
            self.svgBottom = (parseFloat(self.parentDiv.clientHeight) - e.transform.y) / e.transform.k - self.params.paddingTop;
            self.updateFutureLoadGraphCamera(0);
            self.containerGroup.attr("transform", self.newTransform);
            self.topbarComponent.showHomeButton = self.newTransform == d3.zoomIdentity;
            self.updateCulling();
        }

        this.zoom = d3
            .zoom()
            .on("zoom", handleZoom)
            .interpolate(d3.interpolate)
            .scaleExtent([this.params.maxZoom, this.params.minZoom]);

        this.svg = d3
            .select(this.parentDiv)
            .append("svg")
            .attr("width", this.parentDiv.clientWidth)
            .attr("height", this.parentDiv.clientHeight)
            .call(this.zoom);

        var self = this;
        this.svg.on("mousemove", function (event) {
            self.assignHandler.mouseMoveSVG(event);
        });

        this.containerGroup = this.svg.append("g");
        this.projectsSubGroup = this.containerGroup.append("g");

        if (this.transform != null) {
            this.svg.call(this.zoom.transform, this.transform.transform)
        }

        this.tooltipVisual = new ToolTipVisual(this);

    }

    //Count the projects which are not culled
    countRenderedProjects() {
        var count = 0;
        this.projectVisuals.forEach(currP => {
            if (currP.checkIfRender()) {
                count += 1;
            }
        })

        console.log("Of the ", this.getUnfinishedProjectCount(), "projects, ", count, "were rendered");
    }

    //Updates the text at the bottom off the sidebar
    updateBottomText() {
        this.topbarComponent.$store.state.SimInfo.SimName = this.simulator.params.simulationName;

        if (this.simulator.params.isManual)
            this.topbarComponent.$store.state.SimInfo.AutoMode = "Manual";
        else
            this.topbarComponent.$store.state.SimInfo.AutoMode = this.simulator.automationMethod;
    }

    updateElements(forceDuration = null) {
        if (this.topbarComponent.$route.name.toLowerCase() != "pipeline") {
            return;
        }
        this.topbarComponent.updateTimePassedString(this.simulator.hoursPassed);
        this.topbarComponent.updateSimInfo(this.simulator);
        this.topbarComponent.updatePlayButton();

        this.updateBottomText();
        var duration = this.params.duration;

        if(forceDuration != null){
            duration = forceDuration
        }

        if (this.simulator.params.allowPortfolioPlanning && this.state.futureLoadGraph != null && this.state.futureLoadGraph.x != null) {
            this.state.futureLoadGraph.updateTodayLine(duration);
            this.state.futureLoadGraph.updateGraphPossibility()
            this.updateFutureLoadGraphCamera(duration);
        }
        //Update resources
        for (const [name, currVisual] of Object.entries(this.resourceGroupVisuals)) {
            currVisual.updateElements(duration);
        }

        //UpdateGrid
        this.miscVisuals.updateElements(duration);
        //Update projects
        this.projectVisuals.forEach(currVisual => {
            currVisual.updateElements(duration);
        })

        if (this.lastFinishedProjectAmount != this.getFinishedProjectsAmount()) {
            this.sortProjects();
            this.lastFinishedProjectAmount = this.getFinishedProjectsAmount();
        }

        this.addNewProjects();
        
        if(this.state.portfolioSaveComponent != null){
            this.state.portfolioSaveComponent.disableSaveButton = this.simulator.hoursPassed != 0;
        }

        if(this.state.adminSimulateComponent != null){
            this.state.adminSimulateComponent.disableAdminButton = this.simulator.hoursPassed != 0;
        }   
        this.tooltipVisual.updateToolTipVisual();
    }
}

module.exports = { Visualiser };
